import * as THREE from 'three'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Canvas, extend, useFrame } from '@react-three/fiber'
import { Image, ScrollControls, useScroll, Billboard, Text } from '@react-three/drei'
import { suspend } from 'suspend-react'
import { easing, geometry } from 'maath'

extend(geometry)
const playfair = import('@pmndrs/assets/fonts/inter_regular.woff') // Will be replaced with Playfair Display

const CUSTOM_HOVER_TITLES = [
  "Tailor-Made Itineraries Instantly",
  "Explore Any City Like a Local",
  "GPS-Activated Audio Walks",
  "Offline Maps & Audio Access", 
  "Uncover Hidden Gems",
  "Stop Losing Hours Planning",
  "The AI Travel Companion",
  "Smart Route Optimization",
  "Hyper-Personalized Guidance",
  "Instant Answers with Chatbot Sofia",
  "Audio Guides for 1500+ Destinations",
  "Proactive Travel Alerts",
  "Dynamic Storytelling, Simplified",
  "Seamless Cross-Country Exploration",
  "Trusted by Real Travelers",
  "Storytelling for Every Monument",
  "Culture & Adventure Itineraries",
  "Download Your Free Guide Now",
  "Get Started in Seconds",
  "The Future of Guided Travel",
  "Why Travelers Love Sofia",
  "Never Get Lost Again",
  "Free Forever Features",
  "Upgrade to Offline Freedom",
  "Experience Travel, Not Stress",
  "Plan Your Next Trip in Minutes",
  "Expert-Vetted Local Content",
  "Real-Time Destination Insights",
  "Ask Sofia Anything, Anytime"
];

const IMAGE_FILENAMES = [
  "img2.jpg", "img3.jpg", "img4.jpg", "img5.jpg",
  "img6.jpg", "img7.jpg", "img8.jpg", "img9.jpg", "img10.jpg"
];

export const App = () => (
  <Canvas dpr={[1, 1.5]} camera={{ fov: 75, aspect: 1 }}>
    <ScrollControls pages={4} infinite>
      <Scene position={[0, 1.5, 0]} />
    </ScrollControls>
  </Canvas>
)

function Scene({ children, ...props }) {
  const ref = useRef()
  const scroll = useScroll()
  const [hovered, hover] = useState(null)
  useFrame((state, delta) => {
    ref.current.rotation.y = -scroll.offset * (Math.PI * 2) // Rotate contents
    state.events.update() // Raycasts every frame rather than on pointer-move
    easing.damp3(state.camera.position, [-state.pointer.x * 2, state.pointer.y * 2 + 4.5, 7.2], 0.3, delta)
    state.camera.lookAt(0, 0, 0)
  })
  return (
    <group ref={ref} {...props}>
      <Cards category="Museums" from={0} len={Math.PI / 4} onPointerOver={hover} onPointerOut={hover} />
      <Cards category="Portugal Tours" from={Math.PI / 4} len={Math.PI / 2} position={[0, 0.4, 0]} onPointerOver={hover} onPointerOut={hover} />
      <Cards category="Audioguides" from={Math.PI / 4 + Math.PI / 2} len={Math.PI / 2} onPointerOver={hover} onPointerOut={hover} />
      <Cards category="Ai Tours" from={Math.PI * 1.25} len={Math.PI * 2 - Math.PI * 1.25} position={[0, -0.4, 0]} onPointerOver={hover} onPointerOut={hover} />
      <ActiveCard hovered={hovered} />
    </group>
  )
}

function Cards({ category, data, from = 0, len = Math.PI * 2, radius = 5.25, onPointerOver, onPointerOut, ...props }) {
  const [hovered, hover] = useState(null)
  const amount = Math.round(len * 22)
  const textPosition = from + (amount / 2 / amount) * len
  return (
    <group {...props}>
      <Billboard position={[Math.sin(textPosition) * radius * 1.4, 0.5, Math.cos(textPosition) * radius * 1.4]}>
        <Text font={suspend(playfair).default} fontSize={0.25} anchorX="center" color="black">
          {category}
        </Text>
      </Billboard>
      {Array.from({ length: amount - 3 /* minus 3 images at the end, creates a gap */ }, (_, i) => {
        const angle = from + (i / amount) * len
        return (
          <Card
            key={angle}
            onPointerOver={(e) => (e.stopPropagation(), hover(i), onPointerOver(i))}
            onPointerOut={() => (hover(null), onPointerOut(null))}
            position={[Math.sin(angle) * radius, 0, Math.cos(angle) * radius]}
            rotation={[0, Math.PI / 2 + angle, 0]}
            active={hovered !== null}
            hovered={hovered === i}
            url={`${process.env.PUBLIC_URL}/${IMAGE_FILENAMES[i % IMAGE_FILENAMES.length]}`}
          />
        )
      })}
    </group>
  )
}

function Card({ url, active, hovered, ...props }) {
  const ref = useRef()
  useFrame((state, delta) => {
    const f = hovered ? 1.4 : active ? 1.25 : 1
    easing.damp3(ref.current.position, [0, hovered ? 0.25 : 0, 0], 0.1, delta)
    easing.damp3(ref.current.scale, [1.618 * f, 1 * f, 1], 0.15, delta)
  })
  return (
    <group {...props}>
      <Image ref={ref} transparent radius={0.075} url={url} scale={[1.618, 1, 1]} side={THREE.DoubleSide} />
    </group>
  )
}

function ActiveCard({ hovered, ...props }) {
  const ref = useRef()
  const name = useMemo(() => hovered !== null ? CUSTOM_HOVER_TITLES[hovered % CUSTOM_HOVER_TITLES.length] : '', [hovered])
  useLayoutEffect(() => void (ref.current.material.zoom = 0.8), [hovered])
  useFrame((state, delta) => {
    easing.damp(ref.current.material, 'zoom', 1, 0.5, delta)
    easing.damp(ref.current.material, 'opacity', hovered !== null, 0.3, delta)
  })
  return (
    <Billboard {...props}>
      <Text font={suspend(playfair).default} fontSize={0.5} position={[2.15, 3.85, 0]} anchorX="left" color="black">
        {hovered !== null && `${name}\n${hovered}`}
      </Text>
      <Image ref={ref} transparent radius={0.3} position={[0, 1.5, 0]} scale={[3.5, 1.618 * 3.5, 0.2, 1]} url={`${process.env.PUBLIC_URL}/${IMAGE_FILENAMES[hovered % IMAGE_FILENAMES.length]}`} />
    </Billboard>
  )
}
